/**
 * "+ New" dropdown — Content (→ /course/new) and, where Role Play is switched
 * on, Role Play (→ /roleplay/new). Click-outside / Esc close.
 *
 * Moved out of HomePage (29 Sep 2026) so the Courses page can show the same
 * menu: with the app shell, "+ New" sits on the Home and Courses pages rather
 * than in the header.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen, ChevronDown, MessageSquareText, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FEATURE_ROLE_PLAY_UI_VISIBLE } from '@/lib/featureFlags'
import { spaLink } from '@/lib/spaLink'
import { useInShell } from '@/components/shell/inShell'

/** `tall`: the Courses page inside the app shell — a 44px pill (12 Oct 2026). */
/** `pill`: the mobile Courses page (design 8b) — a 40px purple pill, “+ New”, no chevron. */
export function NewMenu({ tall = false, pill = false }: { tall?: boolean; pill?: boolean } = {}) {
  const navigate = useNavigate()
  const inShell = useInShell()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapperRef} className="relative">
      {pill ? (
        <button onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
          style={{ height: 40, borderRadius: 99, background: '#7B3FE4', color: '#fff', fontSize: 14.5, fontWeight: 600, padding: '0 14px 0 12px', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <Plus size={15} strokeWidth={2.4} /> New
        </button>
      ) : <Button
        size="sm"
        className={`gap-2 text-sm bg-[#9333EA] hover:bg-[#7E22CE] text-white font-medium${tall ? ' h-11 px-5 rounded-full shadow-[0_6px_16px_rgba(147,51,234,.28)]' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <Plus className="w-4 h-4" />
        New
        <ChevronDown className="w-3.5 h-3.5 -ml-0.5" />
      </Button>}

      {open && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-20 overflow-hidden">
          <MenuItem
            icon={<BookOpen className="w-4 h-4 text-[#9333EA]" />}
            title={inShell ? 'Course' : 'Content'}
            subtitle="A new course / module"
            href="/course/new"
            onClick={() => { setOpen(false); navigate('/course/new') }}
          />
          {FEATURE_ROLE_PLAY_UI_VISIBLE && (
            <MenuItem
              icon={<MessageSquareText className="w-4 h-4 text-[#F97316]" />}
              title="Role Play"
              subtitle="A voice role-play with a coach"
              href="/roleplay/new"
              onClick={() => { setOpen(false); navigate('/roleplay/new') }}
            />
          )}
        </div>
      )}
    </div>
  )
}

function MenuItem({
  icon, title, subtitle, onClick, href,
}: {
  icon: ReactNode
  title: string
  subtitle: string
  onClick: () => void
  /** A real link: Cmd/Ctrl/middle-click opens it in a new tab. */
  href: string
}) {
  return (
    <a
      {...spaLink(href, onClick)}
      className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left"
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-gray-900">{title}</span>
        <span className="block text-[11px] text-gray-500 truncate">{subtitle}</span>
      </span>
    </a>
  )
}

